import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Navbar.css';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../config/contact';

const NAV_ITEMS = [
  {
    title: 'Industrias',
    path: '/industrias',
    description: 'Soluciones con contexto para cada sector.',
    submenuColumns: [
      [
        { title: 'Automotriz', path: '/industrias/automotriz' },
        { title: 'Banca', path: '/industrias/banca' },
        { title: 'Mercados de Capital', path: '/industrias/mercados-capital' },
        { title: 'Bienes de Consumo', path: '/industrias/bienes-consumo' },
      ],
      [
        { title: 'Energía', path: '/industrias/energia' },
        { title: 'Salud y Ciencias de la Vida', path: '/industrias/salud' },
        { title: 'Seguros', path: '/industrias/seguros' },
        { title: 'Manufactura', path: '/industrias/manufactura' },
      ],
      [
        { title: 'Medios', path: '/industrias/medios' },
        { title: 'Tecnología', path: '/industrias/tecnologia' },
        { title: 'Petróleo y Gas', path: '/industrias/petroleo' },
        { title: 'Retail', path: '/industrias/retail' },
      ],
    ],
  },
  {
    title: 'Servicios',
    path: '/servicios',
    description: 'Capacidades para diseñar, construir y escalar.',
    submenuColumns: [
      [
        { title: 'Soluciones Cloud', path: '/servicios/cloud', description: 'Infraestructura segura y escalable.' },
        { title: 'Analítica de Datos', path: '/servicios/data', description: 'Tableros, indicadores y decisiones claras.' },
        { title: 'Diseño UX/UI', path: '/servicios/design', description: 'Interfaces simples y accesibles.' },
      ],
      [
        { title: 'Ingeniería de Software', path: '/servicios/ingenieria', description: 'Plataformas, APIs e integraciones.' },
        { title: 'Desarrollo Frontend', path: '/servicios/frontend', description: 'Sitios y aplicaciones web rápidas.' },
        { title: 'Estrategia de Innovación', path: '/servicios/innovacion', description: 'Ideas convertidas en soluciones medibles.' },
      ],
    ],
  },
  { title: 'Proyectos', path: '/proyectos' },
  { title: 'Nosotros', path: '/nosotros' },
  { title: 'Contacto', path: '/contacto' },
];

const DESKTOP_QUERY = '(min-width: 961px)';
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';

const matches = (query) => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(query).matches
);

function Chevron({ className = '' }) {
  return (
    <svg className={className} width='10' height='10' viewBox='0 0 10 10' aria-hidden='true' focusable='false'>
      <path d='M1.5 3.5 5 7l3.5-3.5' fill='none' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
}

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState({});
  const closeTimerRef = useRef(null);
  const triggerRefs = useRef([]);
  const panelRefs = useRef([]);
  const mobileToggleRef = useRef(null);
  const mobilePanelRef = useRef(null);
  const location = useLocation();

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openSubmenu = useCallback((index) => {
    clearCloseTimer();
    setActiveSubmenu(index);
  }, [clearCloseTimer]);

  const scheduleSubmenuClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setActiveSubmenu(null);
    }, 240);
  }, [clearCloseTimer]);

  const closeNavigation = useCallback(() => {
    clearCloseTimer();
    setMobileOpen(false);
    setActiveSubmenu(null);
    setMobileSubmenuOpen({});
  }, [clearCloseTimer]);

  const closeSubmenuAndFocus = useCallback((index) => {
    clearCloseTimer();
    setActiveSubmenu(null);
    triggerRefs.current[index]?.focus();
  }, [clearCloseTimer]);

  const closeMobileAndFocus = useCallback(() => {
    setMobileOpen(false);
    setMobileSubmenuOpen({});
    mobileToggleRef.current?.focus();
  }, []);

  const toggleMobileSubmenu = (index) => {
    setMobileSubmenuOpen((current) => ({
      ...current,
      [index]: !current[index],
    }));
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    const handleResize = () => {
      if (matches(DESKTOP_QUERY)) {
        setMobileOpen(false);
        setMobileSubmenuOpen({});
      } else {
        setActiveSubmenu(null);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  useEffect(() => {
    closeNavigation();
  }, [closeNavigation, location.pathname]);

  // Bloqueo de scroll del body mientras el menú móvil está abierto.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  // Esc cierra el megamenú o el panel móvil y devuelve el foco al control que lo abrió.
  useEffect(() => {
    if (activeSubmenu === null && !mobileOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (mobileOpen) closeMobileAndFocus();
      else if (activeSubmenu !== null) closeSubmenuAndFocus(activeSubmenu);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeSubmenu, mobileOpen, closeMobileAndFocus, closeSubmenuAndFocus]);

  useEffect(() => {
    if (mobileOpen) {
      mobilePanelRef.current?.querySelector('a, button')?.focus();
    }
  }, [mobileOpen]);

  // Clic fuera del megamenú lo cierra.
  useEffect(() => {
    if (activeSubmenu === null) return undefined;
    const handlePointerDown = (event) => {
      const trigger = triggerRefs.current[activeSubmenu];
      const panel = panelRefs.current[activeSubmenu];
      if (trigger?.contains(event.target) || panel?.contains(event.target)) return;
      setActiveSubmenu(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [activeSubmenu]);

  const handleTriggerKeyDown = (event, index) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openSubmenu(index);
      window.setTimeout(() => {
        panelRefs.current[index]?.querySelector('a')?.focus();
      }, 0);
    }
  };

  const handleItemBlur = (event, index) => {
    const next = event.relatedTarget;
    if (!next) return;
    const trigger = triggerRefs.current[index];
    const panel = panelRefs.current[index];
    if (trigger?.contains(next) || panel?.contains(next)) return;
    setActiveSubmenu((current) => (current === index ? null : current));
  };

  // Trampa de foco simple: Tab cicla entre el botón de cierre y el panel móvil.
  const handleMobileKeyDown = (event) => {
    if (event.key !== 'Tab' || !mobilePanelRef.current) return;
    const focusables = [
      mobileToggleRef.current,
      ...mobilePanelRef.current.querySelectorAll('a[href], button:not([disabled])'),
    ].filter((el) => el && el.getClientRects().length > 0);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const megamenuOpen = activeSubmenu !== null;

  return (
    <header
      className={[
        'vnav',
        scrolled ? 'is-scrolled' : '',
        megamenuOpen ? 'has-megamenu' : '',
        mobileOpen ? 'has-mobile' : '',
      ].filter(Boolean).join(' ')}
      onKeyDown={mobileOpen ? handleMobileKeyDown : undefined}
    >
      <nav className='vnav-bar' aria-label='Navegación principal'>
        <Link to='/' className='vnav-logo' aria-label='Vekian Labs, inicio'>
          <img src='/vekianlabs-icon.png' alt='' width='20' height='20' className='vnav-logo-mark' />
          <span>Vekian Labs</span>
        </Link>

        <ul className='vnav-menu'>
          {NAV_ITEMS.map((item, index) => {
            const hasSubmenu = Boolean(item.submenuColumns);
            const isOpen = activeSubmenu === index;

            if (!hasSubmenu) {
              return (
                <li className='vnav-item' key={item.path}>
                  <NavLink to={item.path} className='vnav-link'>{item.title}</NavLink>
                </li>
              );
            }

            const detailed = item.submenuColumns.some((column) => column.some((sub) => sub.description));

            return (
              <li
                key={item.path}
                className={`vnav-item has-submenu ${isOpen ? 'is-open' : ''}`}
                onMouseEnter={() => { if (matches(HOVER_QUERY)) openSubmenu(index); }}
                onMouseLeave={() => { if (matches(HOVER_QUERY)) scheduleSubmenuClose(); }}
                onBlur={(event) => handleItemBlur(event, index)}
              >
                <button
                  type='button'
                  ref={(el) => { triggerRefs.current[index] = el; }}
                  className='vnav-link vnav-trigger'
                  aria-expanded={isOpen}
                  aria-controls={`vnav-panel-${index}`}
                  onClick={() => (isOpen ? setActiveSubmenu(null) : openSubmenu(index))}
                  onKeyDown={(event) => handleTriggerKeyDown(event, index)}
                >
                  {item.title}
                  <Chevron className='vnav-chevron' />
                </button>

                <div
                  id={`vnav-panel-${index}`}
                  ref={(el) => { panelRefs.current[index] = el; }}
                  className='vnav-panel'
                  aria-hidden={!isOpen}
                  onMouseEnter={clearCloseTimer}
                >
                  <div className='vnav-panel-inner'>
                    <div className='vnav-panel-intro'>
                      <p className='vnav-panel-label'>{item.title}</p>
                      <p className='vnav-panel-description'>{item.description}</p>
                      <Link to={item.path} className='vk-link vnav-panel-all' tabIndex={isOpen ? 0 : -1}>
                        Ver todo en {item.title}
                      </Link>
                    </div>

                    <div className={`vnav-panel-grid ${detailed ? 'is-detailed' : ''}`}>
                      {item.submenuColumns.map((column, columnIndex) => (
                        <ul className='vnav-panel-column' key={`${item.path}-${columnIndex}`}>
                          {column.map((subitem) => (
                            <li key={subitem.path}>
                              <Link to={subitem.path} className='vnav-panel-link' tabIndex={isOpen ? 0 : -1}>
                                <span className='vnav-panel-link-title'>{subitem.title}</span>
                                {subitem.description && (
                                  <span className='vnav-panel-link-desc'>{subitem.description}</span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ))}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <Link to='/contacto' className='vk-btn vk-btn--primary vk-btn--sm vnav-cta'>
          Agendar cita
        </Link>

        <button
          type='button'
          ref={mobileToggleRef}
          className='vnav-burger'
          onClick={() => setMobileOpen((current) => !current)}
          aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={mobileOpen}
          aria-controls='vnav-mobile'
        >
          <span className='vnav-burger-line' aria-hidden='true'></span>
          <span className='vnav-burger-line' aria-hidden='true'></span>
        </button>
      </nav>

      <div className='vnav-scrim' aria-hidden='true' onClick={() => setActiveSubmenu(null)}></div>

      <div id='vnav-mobile' ref={mobilePanelRef} className='vnav-mobile' role='dialog' aria-modal='true' aria-label='Menú' aria-hidden={!mobileOpen}>
        <div className='vnav-mobile-inner'>
          <ul className='vnav-mobile-list'>
            {NAV_ITEMS.map((item, index) => {
              const hasSubmenu = Boolean(item.submenuColumns);
              const isOpen = Boolean(mobileSubmenuOpen[index]);

              if (!hasSubmenu) {
                return (
                  <li className='vnav-mobile-item' key={item.path}>
                    <Link to={item.path} className='vnav-mobile-link'>{item.title}</Link>
                  </li>
                );
              }

              return (
                <li className={`vnav-mobile-item ${isOpen ? 'is-open' : ''}`} key={item.path}>
                  <button
                    type='button'
                    className='vnav-mobile-link vnav-mobile-toggle'
                    onClick={() => toggleMobileSubmenu(index)}
                    aria-expanded={isOpen}
                    aria-controls={`vnav-mobile-sub-${index}`}
                  >
                    {item.title}
                    <Chevron className='vnav-mobile-chevron' />
                  </button>

                  <div id={`vnav-mobile-sub-${index}`} className='vnav-mobile-sub' hidden={!isOpen}>
                    <ul>
                      <li>
                        <Link to={item.path} className='vnav-mobile-sublink is-all'>
                          Ver todo en {item.title}
                        </Link>
                      </li>
                      {item.submenuColumns.flat().map((subitem) => (
                        <li key={subitem.path}>
                          <Link to={subitem.path} className='vnav-mobile-sublink'>{subitem.title}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className='vnav-mobile-footer'>
            <Link to='/contacto' className='vk-btn vk-btn--primary vk-btn--lg vnav-mobile-cta'>
              Agendar cita
            </Link>
            <a href={CONTACT_MAILTO} className='vnav-mobile-mail'>{CONTACT_EMAIL}</a>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
